import { identity } from 'lodash';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';

import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import { IconButton } from '@mui/material';

import { MayBeNil } from 'app/@types/utils/may-be-nil';
import { PropsOf } from 'app/@types/utils/props-of';
import { SEARCH_VIEW_COOKIE } from 'app/constants';
import useStores from 'hooks/use-stores';
import useFormattedData from 'pages/Search/components/VulnerabilitiesList/hooks/use-formatted-data';
import { getCookie, setCookie } from 'utils/cookies';
import isBrowser from 'utils/is-browser';

import List from './components/List';
import Table from './components/Table';
import useStyles from './styles';
import { DataItem, View } from './types';

interface Props {
    total: number;
    items: DataItem[];
    exact?: MayBeNil<DataItem>;
    sort?: PropsOf<typeof Table>['sort'];
    children?: (content: React.ReactNode) => React.ReactNode;
}

const VulnerabilitiesList: React.FC<Props> = ({ total, items, exact, sort, children = identity }) => {
    const { classes, cx } = useStyles();
    const { searchStore } = useStores();
    const data = useFormattedData({ items, exact });
    useMemo(() => {
        if (isBrowser()) {
            const view = getCookie(SEARCH_VIEW_COOKIE);
            if (view) {
                searchStore.setView(view);
            }
        }
    }, [searchStore]);

    const content =
        searchStore.view === View.LIST ? (
            <List data={data} />
        ) : searchStore.view === View.TABLE ? (
            <Table data={data} sort={sort} />
        ) : null;

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <p className={classes.total}>{`${total} matches found`}</p>
                <div className={classes.buttons}>
                    <IconButton
                        className={cx(classes.button, { [classes.active]: searchStore.view === View.LIST })}
                        onClick={() => {
                            searchStore.setView(View.LIST);
                            setCookie(SEARCH_VIEW_COOKIE, View.LIST, { ['max-age']: Number.MAX_SAFE_INTEGER });
                        }}
                    >
                        <TableRowsOutlinedIcon />
                    </IconButton>
                    <IconButton
                        className={cx(classes.button, { [classes.active]: searchStore.view === View.TABLE })}
                        onClick={() => {
                            searchStore.setView(View.TABLE);
                            setCookie(SEARCH_VIEW_COOKIE, View.TABLE, { ['max-age']: Number.MAX_SAFE_INTEGER });
                        }}
                    >
                        <MenuOutlinedIcon />
                    </IconButton>
                </div>
            </div>

            {children(content)}
        </div>
    );
};

export default observer(VulnerabilitiesList);
